import type { ApplicationAbstract, VariableADTvalue } from '@lighthouse/core'
import type { AppLoginMode } from '@lighthouse/shared'
import {
    AccountPage as AccountPageContainer,
    ApplicationContainer,
    AppThemeContextProvider,
    fieldConvertValue,
    getBackgroundStyle,
    PAGE_CONTAINER_HOST_ID,
    useAtomAction,
    useAtomData,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { mergeDeepRight } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { setLanguageAtom } from '@/atoms/application/action'
import { AppSystemPageAtom, isMobileDeviceAtom, otherPlatformListAtom } from '@/atoms/application/state'
import { openPageStackAtom } from '@/atoms/page/action'
import { homePageAtom } from '@/atoms/page/state'
import { stackFactory } from '@/atoms/page/utils'
import { PageTool } from '@/components/PageTool'
import { useApplication, useCurrentBreakPoint, useCurrentEnvId, useLanguage } from '@/hooks/useApplication'
import { useDataSource, useDataSourceRoles } from '@/hooks/useDataSource'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useToggleLang } from '@/hooks/useToggleLang'

interface RegisterProps {}

const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
`

const SCxContent = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    max-height: 100%;
    overflow: hidden;
    background-color: var(--color-gray-50);
`

const SCxWrapper = styled.div`
    margin: 46px 24px 38px 24px;
    display: flex;
    flex-direction: column;
    height: calc(100% - 44px);
    border-radius: 12px;
    background-color: transparent;
    box-shadow: none;

    #page-scroll-container {
        display: flex;
        align-items: center;
    }
`

const AccountPage: React.FC<RegisterProps> = props => {
    const { run: openPageStack } = useAtomAction(openPageStackAtom)

    const envId = useCurrentEnvId()
    const app = useApplication()
    const {
        id: appId,
        config: { theme, navbar, authentication, webTag }
    } = app
    const { login, background } = authentication
    const dataSource = useDataSource(appId, envId, USER_DATASOURCE)
    const language = useLanguage()
    const breakPoint = useCurrentBreakPoint()
    const isMobile = useAtomData(isMobileDeviceAtom)
    const homePage = useAtomData(homePageAtom)
    // 切换语言
    const { handleToggleLang } = useToggleLang()
    const roles = useDataSourceRoles()
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const wxOfficialAccount = otherPlatformList?.find(v => v.type === 'WECHAT_OFFICIAL_ACCOUNT')
    const isWxOfficialAccount = !!wxOfficialAccount

    const parseVariableImage = useParseVariableWithSubscription(background?.image?.resource)
    const parseBackgroundVariableImage = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariableImage(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        },
        [parseVariableImage]
    )
    const backgroundCss = useMemo(() => {
        const styles = {
            ...getBackgroundStyle({ appId, value: background, palettes: theme.palettes, parseVariableImage: parseBackgroundVariableImage })
        }
        return {
            ...styles,
            backgroundColor: styles.backgroundColor || webTag.backgroundColor
        }
    }, [appId, background, parseBackgroundVariableImage, theme.palettes, webTag.backgroundColor])

    const { run: setLanguage } = useAtomAction(setLanguageAtom)
    const { run: setAppSystemPage } = useAtomAction(AppSystemPageAtom)
    const [mode, setMode] = useState<AppLoginMode>('home')

    const loginApp = useMemo(() => mergeDeepRight<ApplicationAbstract>(app, { config: { navbar: { isShow: false } } }), [app])

    const accountDefaultMode = useMemo(() => {
        const loginWayMap: Record<
            Exclude<AppLoginMode, 'bindMobile' | 'perfect' | 'register' | 'home' | 'bindEmail' | 'bindWechat'>,
            boolean | undefined
        > = {
            weChatLogin: login?.weChat.isOpened,
            emailLogin: login?.email.isOpened,
            mobileLogin: login?.phone.isOpened
        }
        const loginWayList = Object.entries(loginWayMap).filter(([name, isOpened]) => isOpened)
        const loginWay = loginWayList[0]
        if (loginWayList.length === 1 && loginWay[0] !== 'weChatLogin') {
            return loginWay[0] as AppLoginMode
        }
        return 'home'
    }, [login])

    const handleHomeBack = useCallback(() => {
        if (!homePage) {
            return
        }
        const { id: pageId } = homePage
        setAppSystemPage(null)
        openPageStack(stackFactory({ pageId }))
    }, [homePage, openPageStack, setAppSystemPage])

    useEffect(() => {
        if (accountDefaultMode) {
            setMode(accountDefaultMode)
        }
    }, [accountDefaultMode])

    return (
        <SCxContainer>
            <SCxContent>
                <PageTool />
                <AppThemeContextProvider theme={theme}>
                    <SCxWrapper>
                        <ApplicationContainer
                            id={PAGE_CONTAINER_HOST_ID}
                            application={loginApp}
                            breakPoint={breakPoint}
                            previewMode={isMobile ? 'simulationMobile' : 'simulationWebsite'}
                            language={language}
                            onChangeLanguage={handleToggleLang}
                            styles={{
                                desktopHeader: {
                                    display: 'none'
                                },
                                mobileHeader: {
                                    display: 'none'
                                },
                                wrapper: backgroundCss
                            }}
                        >
                            <AccountPageContainer
                                mode={mode}
                                dataSource={dataSource}
                                disableEvent
                                authentication={authentication}
                                language={language}
                                roles={roles}
                                navbar={navbar}
                                isWxOfficialAccount={isWxOfficialAccount}
                                style={{ padding: '0 32px' }}
                                onChangeMode={setMode}
                                onHomeBack={handleHomeBack}
                                onChangeLanguage={v => {
                                    setLanguage(v)
                                    accountDefaultMode && setMode(accountDefaultMode)
                                }}
                            />
                        </ApplicationContainer>
                    </SCxWrapper>
                </AppThemeContextProvider>
            </SCxContent>
        </SCxContainer>
    )
}

export default AccountPage
